/**
 * spiritualJourneyTimeline.js
 * Builds a compact spiritual journey narrative from available data signals.
 *
 * Does NOT require new DB tables. Works from:
 *   - Entity tone signals (stored in relational memory)
 *   - Visit rows from get_visit_timeline
 *   - Entity creation date (as proxy for first pastoral contact)
 *
 * Output: compact narrative string for LLM injection (max 200 chars)
 * or null if there's nothing meaningful to surface.
 */

import { getDaysSince } from "../date";

// ── Spiritual milestones derived from tone/visit patterns ─────────────────────

const TONE_MILESTONES = {
  accompany:       "proceso espiritual activo",
  sensitive:       "situación delicada activa",
  followup_urgent: "ausencia prolongada",
  followup_needed: "seguimiento pendiente",
  neutral:         null,
};

/**
 * Extract meaningful visit milestones from visit rows.
 * Returns at most 2 narrative points about the visit history.
 *
 * @param {Object[]} visitRows
 * @returns {string[]}
 */
function buildVisitMilestones(visitRows) {
  if (!visitRows?.length) return [];
  const milestones = [];

  const sorted = [...visitRows].sort((a, b) => new Date(b.date) - new Date(a.date));
  const total  = sorted.length;

  if (total >= 3) {
    milestones.push(`${total} visitas registradas`);
  }

  const lastDays = getDaysSince(sorted[0]?.date);
  if (lastDays !== null) {
    if (lastDays <= 14) {
      milestones.push("visita reciente");
    } else if (lastDays > 90) {
      milestones.push(`sin visita hace ${lastDays} días`);
    }
  }

  return milestones;
}

/**
 * Build a compact spiritual journey narrative for LLM injection.
 * Returns null if the history is empty or the situation is unremarkable.
 *
 * @param {{
 *   history:      import("./relationalMemoryEngine").RelationalRecord | null,
 *   visitRows:    Object[] | null,
 *   entityType:   "family" | "person" | "church",
 *   currentTone:  string,
 * }} params
 * @returns {string | null}
 */
export function buildJourneyNarrative({ history, visitRows, entityType, currentTone }) {
  const signals    = history?.signals ?? [];
  const milestones = [];

  // ── Visit milestones from real data ──────────────────────────────────────
  const visitMilestones = buildVisitMilestones(visitRows);
  milestones.push(...visitMilestones);

  // ── Tone-based milestone ──────────────────────────────────────────────────
  const toneLabel = TONE_MILESTONES[currentTone];
  if (toneLabel) milestones.push(toneLabel);

  // ── Cross-session trend (from relational memory) ──────────────────────────
  if (signals.length >= 2) {
    const oldest   = signals[signals.length - 1];
    const newest   = signals[0];
    const daySpan  = Math.round((newest.ts - oldest.ts) / 86_400_000);

    if (daySpan >= 7) {
      // Did tone improve or worsen?
      const RISK_TONES = new Set(["followup_urgent", "sensitive"]);
      const wasRisk = RISK_TONES.has(oldest.tone);
      const isRisk  = RISK_TONES.has(newest.tone);

      if (wasRisk && !isRisk) {
        milestones.push("mejoría en el seguimiento");
      } else if (!wasRisk && isRisk) {
        milestones.push("señal de alejamiento detectada recientemente");
      } else if (wasRisk && isRisk) {
        milestones.push(`situación delicada persistente por ${daySpan} días`);
      }
    }
  }

  // Return null for neutral/unremarkable histories
  if (milestones.length === 0) return null;
  if (milestones.length === 1 && milestones[0] === "visita reciente") return null;

  const entityLabel = entityType === "family" ? "Familia" : entityType === "person" ? "Persona" : "Iglesia";
  const narrative = milestones.filter(Boolean).join("; ");
  return `${entityLabel}: ${narrative}.`;
}
